import {
  Color,
  DoubleSide,
  ExtrudeGeometry,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Object3DEventMap,
  SRGBColorSpace,
  Scene,
  Shape,
  ShapeGeometry,
  Vector2,
} from "three";
import { generateUUID } from "three/src/math/MathUtils.js";
import { CameraInterface } from "../engine/Engine";
import { LoaderManager } from "../loader/Loader";
import { SugarTextureLoader } from "../loader/SugarTextureLoader";
import { renderSettingsStore } from "../core/pipeline/RenderSettingsStore";
import { Corner } from "./Corner";
import { Point } from "./Point";

export class Polygon extends Point {
  private _floorMaterial?: Material;
  private _structureMaterial?: Material;

  constructor(points: Point[]) {
    super();

    const uuid = generateUUID();
    this._type = "Floor";
    this._name = "Floor_" + generateUUID();
    this._root = new Object3D();
    this._root.name = "Polygon_" + uuid;
    this._isDirty = true;

    this.setPoints(points);

    this._floorMaterial = new MeshStandardMaterial({
      color: new Color(0xffffff),
      side: DoubleSide,
    });

    this._structureMaterial = new MeshStandardMaterial({
      color: new Color(0xaaaaaa),
      side: DoubleSide,
    });

    const loader = new LoaderManager(new SugarTextureLoader());
    loader
      .load(
        "https://s3.eu-central-1.amazonaws.com/cdn.sugartech/mottobucket/CDN/sugar-viewer/2145.jpg"
      )
      .then((texture) => {
        texture.colorSpace = SRGBColorSpace;
        (this._floorMaterial! as MeshStandardMaterial).map = texture;
        renderSettingsStore.applyMaterial(this._floorMaterial!);
        this._floorMaterial!.needsUpdate = true;
      });
  }

  private setPoints(points: Point[]) {
    points.forEach((p) => {
      this.unbindJoint(p);
      this.bindJoint(p);
    });
  }

  private createPolygonParameters() {
    const corners = this._joints?.filter((j) => j instanceof Corner);
    const points = corners?.map((p) => {
      return p.point;
    });
    const midPoint = points?.reduce((p, c) => {
      return p.clone().add(c);
    });

    const position = midPoint?.multiplyScalar(1.0 / points!.length);
    const localvertices = points?.map((p) => {
      return p.clone().sub(position!);
    });

    return { position: position, vertices: localvertices };
  }

  private _floorMesh: Mesh = new Mesh();
  private _floorStructureMesh: Mesh = new Mesh();

  get root(): Object3D<Object3DEventMap> | undefined {
    if (!this.isDirty) {
      return this._root;
    }
    if (this._floorMesh) {
      this._floorMesh.removeFromParent();
      this._floorStructureMesh.removeFromParent();
    }

    this._floorMesh?.geometry.dispose();

    if (this._floorMesh === null || this._floorMesh === undefined) {
      this._floorMesh = new Mesh();
    }

    this._floorMesh.material = this._floorMaterial!;

    const { position, vertices } = this.createPolygonParameters();
    this._root?.position.copy(position!);
    //this._floorMesh.position.copy(position!);
    this._floorMesh.rotation.set(Math.PI * 0.5, 0, 0);

    const shape = new Shape(vertices?.map((e) => new Vector2(e.x, e.z)));
    this._floorMesh.geometry = new ShapeGeometry(shape);
    this._root?.add(this._floorMesh);

    const extrudeSettings = {
      steps: 1,
      depth: 0.2,
      bevelEnabled: false,
      bevelThickness: 0.0,
      bevelSize: 0,
      bevelOffset: 0,
      bevelSegments: 0,
    };

    //this._floorStructureMesh.position.copy(position!);
    this._floorStructureMesh.position.setY(
      /*this._floorStructureMesh.position.y*/ -0.01
    );
    this._floorStructureMesh.rotation.set(Math.PI * 0.5, 0, 0);

    const geometry = new ExtrudeGeometry(shape, extrudeSettings);

    this._floorStructureMesh.geometry?.dispose();
    this._floorStructureMesh.geometry = geometry;
    this._floorStructureMesh.material = this._structureMaterial!;
    this._root?.add(this._floorStructureMesh);

    return this._root;
  }

  render3d(scene?: Scene, camera?: CameraInterface): void {
    scene?.add(this.root!);
  }

  render2d(scene?: Scene, camera?: CameraInterface): void {
    this.render3d(scene, camera);
  }

  dispose(scene?: Scene): void {
    super.dispose();
    this._floorMesh?.removeFromParent();
    this._floorMesh?.traverse((m) => {
      if (m instanceof Mesh) m.geometry?.dispose();
    });
    this._floorStructureMesh.removeFromParent();
    this._floorStructureMesh?.traverse((m) => {
      if (m instanceof Mesh) m.geometry?.dispose();
    });
    this._root?.removeFromParent();
  }
}
